// src/pages/Platform/Admin/AffiliateManager.js
import React, { useState, useEffect } from 'react';
import { auth } from '../../../firebase/config/config';
import { 
  Users, DollarSign, Plus, CheckCircle, ShieldAlert, 
  ExternalLink, Check, Copy, AlertCircle, RefreshCw 
} from 'lucide-react';
import './AffiliateManager.css';

export default function AffiliateManager() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ summary: {}, affiliates: [] });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(null); // holds affiliate object
  const [payoutNotes, setPayoutNotes] = useState('');
  const [submittingPayout, setSubmittingPayout] = useState(false);

  // Form state for creating affiliate
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    referralCode: '',
    payoutMethod: 'paypal',
    payoutDetails: '',
    isDefaultFallback: false,
    notes: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in as admin');

      const token = await user.getIdToken();
      const res = await fetch('/api/affiliates/admin/overview', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to fetch affiliate overview');

      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    try {
      setCreateLoading(true);
      setCreateError(null);
      const user = auth.currentUser;
      const token = await user.getIdToken();

      const res = await fetch('/api/affiliates/admin/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          payoutDetails: { account: formData.payoutDetails }
        })
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to create affiliate');

      setShowCreateModal(false);
      setFormData({
        name: '',
        email: '',
        referralCode: '',
        payoutMethod: 'paypal',
        payoutDetails: '',
        isDefaultFallback: false,
        notes: ''
      });
      fetchOverview();
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!showPayoutModal) return;
    try {
      setSubmittingPayout(true);
      const user = auth.currentUser;
      const token = await user.getIdToken();

      const res = await fetch('/api/affiliates/admin/payouts/mark-paid', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          affiliateId: showPayoutModal.id,
          payoutNotes
        })
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to mark payouts as paid');

      setShowPayoutModal(null);
      setPayoutNotes('');
      fetchOverview();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmittingPayout(false);
    }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/?ref=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="admin-affiliate-container">
      {/* Top Banner */}
      <div className="admin-affiliate-header">
        <div>
          <h1>Affiliate Program Management</h1>
          <p>Configure partners, manage commission tiers, and track customer attribution.</p>
        </div>
        <div className="header-actions">
          <button onClick={fetchOverview} className="btn-secondary" title="Refresh">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <Plus size={16} /> Add Partner
          </button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="admin-metrics-grid">
        <div className="admin-metric-card">
          <span className="metric-label">Active Partners</span>
          <div className="metric-num">{data.summary?.total_affiliates || 0}</div>
          <Users size={20} className="card-icon blue" />
        </div>
        <div className="admin-metric-card">
          <span className="metric-label">Attributed Customers</span>
          <div className="metric-num">{data.summary?.total_referred_customers || 0}</div>
          <CheckCircle size={20} className="card-icon green" />
        </div>
        <div className="admin-metric-card highlight-card">
          <span className="metric-label">Pending Payouts</span>
          <div className="metric-num">${Number(data.summary?.total_pending_payouts || 0).toFixed(2)}</div>
          <DollarSign size={20} className="card-icon yellow" />
        </div>
        <div className="admin-metric-card">
          <span className="metric-label">Total Paid Out</span>
          <div className="metric-num">${Number(data.summary?.total_paid_out || 0).toFixed(2)}</div>
          <DollarSign size={20} className="card-icon purple" />
        </div>
      </div>

      {/* Partners List */}
      <div className="affiliates-list-card">
        <div className="list-card-header">
          <h3>Registered Affiliates & Partners</h3>
        </div>

        {loading ? (
          <div className="loading-state">Loading affiliates...</div>
        ) : error ? (
          <div className="error-state"><AlertCircle size={16} /> {error}</div>
        ) : data.affiliates.length === 0 ? (
          <div className="empty-state">
            <p>No affiliates created yet. Click "Add Partner" to set up your marketing collaborator.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-affiliates-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Referral Slug</th>
                  <th>Customers</th>
                  <th>Pending Balance</th>
                  <th>Total Paid</th>
                  <th>Fallback Attribution</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.affiliates.map((aff) => (
                  <tr key={aff.id}>
                    <td>
                      <div className="partner-name-cell">
                        <strong>{aff.name}</strong>
                        <span>{aff.email}</span>
                      </div>
                    </td>
                    <td>
                      <div className="slug-badge-cell">
                        <code>?ref={aff.referral_code}</code>
                        <button 
                          onClick={() => copyLink(aff.referral_code)} 
                          className="copy-mini-btn"
                          title="Copy referral link"
                        >
                          {copiedCode === aff.referral_code ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                        </button>
                      </div>
                    </td>
                    <td>
                      <strong>{aff.total_referrals || 0}</strong>
                    </td>
                    <td>
                      <span className="pending-pill">
                        ${Number(aff.pending_amount || 0).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      ${Number(aff.paid_amount || 0).toFixed(2)}
                    </td>
                    <td>
                      {aff.is_default_fallback ? (
                        <span className="fallback-badge active" title="Receives unreferred store traffic">
                          ✓ Default Fallback
                        </span>
                      ) : (
                        <span className="fallback-badge inactive">Link-only</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <a
                          href={`/affiliates?code=${aff.referral_code}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn view"
                          title="View Partner Portal"
                        >
                          <ExternalLink size={14} /> Portal
                        </a>
                        {Number(aff.pending_amount || 0) > 0 && (
                          <button
                            onClick={() => setShowPayoutModal(aff)}
                            className="action-btn pay"
                          >
                            Pay ${Number(aff.pending_amount).toFixed(2)}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Create Partner */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h2>Add New Affiliate Partner</h2>
            <p>Set up an affiliate or marketing partner to track their directly referred customers.</p>

            <form onSubmit={handleCreateAffiliate} className="modal-form">
              <div className="form-group">
                <label>Partner Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Alex Marketing"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Partner Email *</label>
                <input
                  type="email"
                  required
                  placeholder="alex@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Custom Referral Slug * (e.g. 'partner')</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. alex"
                  value={formData.referralCode}
                  onChange={(e) => setFormData({ ...formData, referralCode: e.target.value })}
                />
                <span className="field-hint">Referral link will be: smushlabs.ai/?ref={formData.referralCode || 'slug'}</span>
              </div>

              <div className="form-group">
                <label>Payout Method & Account</label>
                <div className="payout-row">
                  <select
                    value={formData.payoutMethod}
                    onChange={(e) => setFormData({ ...formData, payoutMethod: e.target.value })}
                  >
                    <option value="wise">Wise</option>
                    <option value="paypal">PayPal</option>
                    <option value="upi">UPI (India)</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Payout Email or Account #"
                    value={formData.payoutDetails}
                    onChange={(e) => setFormData({ ...formData, payoutDetails: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isDefaultFallback}
                    onChange={(e) => setFormData({ ...formData, isDefaultFallback: e.target.checked })}
                  />
                  <span>
                    <strong>Default Fallback Partner:</strong> Automatically attribute any direct/unreferred customer sales to this partner (ideal for landing page revenue-sharing).
                  </span>
                </label>
              </div>

              {createError && (
                <div className="modal-error"><ShieldAlert size={16} /> {createError}</div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={createLoading} className="btn-primary">
                  {createLoading ? 'Creating...' : 'Create Affiliate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Settle Payout */}
      {showPayoutModal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h2>Record Payout for {showPayoutModal.name}</h2>
            <p>
              Confirm you have transferred <strong>${Number(showPayoutModal.pending_amount).toFixed(2)}</strong> via {showPayoutModal.payout_method?.toUpperCase() || 'Wise/PayPal/UPI'}.
            </p>

            <div className="form-group">
              <label>Payment Reference / Transaction ID</label>
              <input
                type="text"
                placeholder="e.g. PayPal TXN-9988123 or Wise Transfer #4412"
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
              />
            </div>

            <div className="modal-actions">
              <button type="button" onClick={() => setShowPayoutModal(null)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleMarkPaid} disabled={submittingPayout} className="btn-primary">
                {submittingPayout ? 'Processing...' : 'Confirm Paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
